import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { Check, ChevronDown, Globe2, Search } from "lucide-react";

/**
 * Ülke seçici: yerel <select> yerine aranabilir, bayraklı açılır liste.
 *
 * WAI-ARIA "combobox + listbox" deseni: tetikleyici düğme paneli açar, odak
 * paneldeki arama kutusuna geçer; ok tuşları seçeneği aria-activedescendant ile
 * gezer (odak kutuda kalır, yazmaya devam edilebilir).
 */

/** Her zaman listenin başında duran ülkeler (ülke sayısından bağımsız). */
const PINNED = ["TR"];
const PANEL_W = 300;
/** Mobil kenar boşluğu: panel ekran kenarına yapışmasın. */
const GUTTER = 16;

type Option = { code: string | null; n: number };
type Country = { code: string; n: number; name: string; hay: string };

// DisplayNames her çağrıda kurulursa ~240 ülkede fark edilir; tek örnek yeter.
// Eski tarayıcıda yoksa ya da kod tanınmazsa kodun kendisi gösterilir.
let trNames: Intl.DisplayNames | null | undefined;
let enNames: Intl.DisplayNames | null | undefined;
function displayName(kind: "tr" | "en", code: string): string | null {
  try {
    if (kind === "tr") trNames ??= new Intl.DisplayNames(["tr"], { type: "region", fallback: "none" });
    else enNames ??= new Intl.DisplayNames(["en"], { type: "region", fallback: "none" });
    const dn = kind === "tr" ? trNames : enNames;
    const name = dn?.of(code);
    // "ZZ" gibi kodlara CLDR "Bilinmeyen Bölge" diyor; ad yerine kodu göstermek daha dürüst.
    return name && name !== code && code !== "ZZ" ? name : null;
  } catch {
    if (kind === "tr") trNames = null;
    else enNames = null;
    return null;
  }
}

/**
 * Arama için sadeleştirme: Türkçe küçük harf (İ→i, I→ı), sonra aksanlar
 * atılır ve ı→i. Böylece "turk", "TÜRK", "türk" hepsi "Türkiye"yi bulur.
 */
function fold(s: string): string {
  return s
    .toLocaleLowerCase("tr")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/ı/g, "i");
}

const fmt = (n: number): string => n.toLocaleString("tr-TR");

/**
 * Windows bölgesel gösterge emojisini bayrak olarak çizmiyor ("TR" harfleri
 * çıkıyor), bu yüzden küçük PNG. Yüklenemezse aynı boyutta boş kutu kalır ki
 * satırlar hizasını kaybetmesin.
 */
export function Flag({ code }: { code: string }): ReactNode {
  const [broken, setBroken] = useState(false);
  const box = "inline-block h-[15px] w-5 shrink-0 rounded-[2px] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]";
  if (broken) return <span aria-hidden className={`${box} bg-neutral-800`} />;
  return (
    <span aria-hidden className={`${box} relative overflow-hidden bg-neutral-800`}>
      <img
        src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`}
        alt=""
        width={20}
        height={15}
        loading="lazy"
        decoding="async"
        onError={() => setBroken(true)}
        className="block h-full w-full object-cover"
      />
      {/* Kenar çizgisi resmin ÜSTÜNDE: beyaz zeminli bayraklar (JP, KR) koyu panelde dağılmasın. */}
      <span className="absolute inset-0 rounded-[2px] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]" />
    </span>
  );
}

function AllIcon(): ReactNode {
  return (
    <span aria-hidden className="grid h-[15px] w-5 shrink-0 place-items-center text-neutral-500">
      <Globe2 size={14} strokeWidth={1.75} />
    </span>
  );
}

export function CountryPicker({
  value,
  onChange,
  options,
  className = "",
}: {
  /** ISO-3166 alfa-2, büyük harf; "" = tüm ülkeler. */
  value: string;
  onChange: (code: string) => void;
  /** API'den gelen ülke/istasyon sayıları; code null olanlar atlanır. */
  options: Option[];
  className?: string;
}): ReactNode {
  const id = useId();
  const listId = `${id}-list`;
  const labelId = `${id}-label`;
  const optId = (code: string): string => `${id}-opt-${code || "all"}`;

  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [alignRight, setAlignRight] = useState(false);

  // Adlar ve arama anahtarı yalnızca liste değişince hesaplanır, her tuşta değil.
  const countries = useMemo<Country[]>(() => {
    const seen = new Map<string, Country>();
    for (const o of options) {
      if (!o.code) continue;
      const code = o.code.toUpperCase();
      const prev = seen.get(code);
      if (prev) {
        prev.n += o.n;
        continue;
      }
      const tr = displayName("tr", code);
      const en = displayName("en", code);
      seen.set(code, { code, n: o.n, name: tr ?? code, hay: fold(`${tr ?? ""} ${en ?? ""}`) });
    }
    // Sabitlenen ülke API listesinde yoksa bile görünsün (sayı 0).
    for (const code of PINNED) {
      if (!seen.has(code)) {
        const tr = displayName("tr", code);
        const en = displayName("en", code);
        seen.set(code, { code, n: 0, name: tr ?? code, hay: fold(`${tr ?? ""} ${en ?? ""}`) });
      }
    }
    return [...seen.values()].sort((a, b) => b.n - a.n || a.name.localeCompare(b.name, "tr"));
  }, [options]);

  const total = useMemo(() => countries.reduce((s, c) => s + c.n, 0), [countries]);
  const byCode = useMemo(() => new Map(countries.map((c) => [c.code, c])), [countries]);
  const selected = value ? byCode.get(value.toUpperCase()) : undefined;

  const q = fold(query.trim());
  const searching = q.length > 0;

  /** Görünen seçenekler, klavye gezintisinin sırasıyla aynı düz dizi. "" = tüm ülkeler. */
  const { pinned, rest, flat } = useMemo(() => {
    if (!searching) {
      const pinned = PINNED.map((c) => byCode.get(c)).filter((c): c is Country => !!c);
      const rest = countries.filter((c) => !PINNED.includes(c.code));
      return { pinned, rest, flat: ["", ...pinned.map((c) => c.code), ...rest.map((c) => c.code)] };
    }
    // Sıralama: kod birebir ("tr" → Türkiye), ad başı eşleşmesi, adın içinde geçen.
    const rank = (c: Country): number => {
      if (c.code.toLowerCase() === q) return 0;
      if (c.hay.startsWith(q) || c.hay.includes(` ${q}`)) return 1;
      if (c.hay.includes(q)) return 2;
      return -1;
    };
    const hits = countries
      .map((c) => ({ c, r: rank(c) }))
      .filter((x) => x.r >= 0)
      .sort((a, b) => a.r - b.r || b.c.n - a.c.n)
      .map((x) => x.c);
    return { pinned: [] as Country[], rest: hits, flat: hits.map((c) => c.code) };
  }, [searching, q, countries, byCode]);

  const activeCode = flat[active];

  const close = (focusTrigger: boolean): void => {
    setOpen(false);
    setQuery("");
    if (focusTrigger) trigger.current?.focus();
  };

  const choose = (code: string): void => {
    onChange(code);
    close(true);
  };

  const openPanel = (): void => {
    // Açılışta taşma kontrolü: panel sağdan ekrana sığmıyorsa tetikleyicinin sağ kenarına hizalanır.
    const r = trigger.current?.getBoundingClientRect();
    if (r) setAlignRight(r.left + PANEL_W > window.innerWidth - GUTTER);
    setQuery("");
    setOpen(true);
  };

  // Açılınca etkin seçenek mevcut seçim olur; arama değişince ilk sonuç.
  useLayoutEffect(() => {
    if (!open) return;
    if (searching) setActive(0);
    else setActive(Math.max(0, flat.indexOf(value ? value.toUpperCase() : "")));
    // flat bilerek bağımlılıkta değil: yalnızca açılış ve sorgu değişimi sıfırlar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, q]);

  useEffect(() => {
    if (open) input.current?.focus();
  }, [open]);

  // Etkin seçenek görünür alanda kalsın (klavyeyle listenin dibine inildiğinde).
  useEffect(() => {
    if (!open || activeCode === undefined) return;
    document.getElementById(optId(activeCode))?.scrollIntoView({ block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeCode]);

  // Dışarı tıklama kapatır; odak kullanıcının tıkladığı yere gider, geri çekilmez.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (!root.current?.contains(e.target as Node)) close(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const onInputKey = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    const last = flat.length - 1;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActive((i) => Math.min(last, i + 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActive((i) => Math.max(0, i - 1));
        break;
      case "PageDown":
        e.preventDefault();
        setActive((i) => Math.min(last, i + 8));
        break;
      case "PageUp":
        e.preventDefault();
        setActive((i) => Math.max(0, i - 8));
        break;
      case "Home":
        // Arama kutusunda Home/End imleci taşır; boşken listeyi gezmek daha yararlı.
        if (query) return;
        e.preventDefault();
        setActive(0);
        break;
      case "End":
        if (query) return;
        e.preventDefault();
        setActive(last);
        break;
      case "Enter":
        e.preventDefault();
        if (activeCode !== undefined) choose(activeCode);
        break;
      case "Escape":
        e.preventDefault();
        e.stopPropagation(); // açık bir Modal'ın içindeysek pencere kapanmasın
        close(true);
        break;
      case "Tab":
        // Panel kaybolacağı için odak belirsiz yere düşmesin: tetikleyiciye döner.
        e.preventDefault();
        close(true);
        break;
    }
  };

  const renderOption = (code: string): ReactNode => {
    const c = code ? byCode.get(code) : undefined;
    const isSelected = code === (value ? value.toUpperCase() : "");
    const isActive = code === activeCode;
    const index = flat.indexOf(code);
    return (
      <li
        key={code || "all"}
        id={optId(code)}
        role="option"
        aria-selected={isSelected}
        // Fare seçeneğe basınca odak arama kutusunda kalsın.
        onMouseDown={(e) => e.preventDefault()}
        onMouseMove={() => index !== active && setActive(index)}
        onClick={() => choose(code)}
        className={`mx-1 flex cursor-pointer items-center gap-2.5 rounded-[var(--radius-sm)] px-2.5 py-[7px] text-sm ${
          isActive ? "bg-white/[0.07] shadow-[inset_2px_0_0_var(--color-accent-400)]" : ""
        } ${isSelected ? "text-accent-200" : "text-neutral-200"}`}
      >
        {c ? <Flag code={c.code} /> : <AllIcon />}
        <span className="min-w-0 flex-1 truncate">
          {c ? c.name : "Tüm ülkeler"}
          {c && c.name !== c.code && <span className="sr-only"> ({c.code})</span>}
        </span>
        <span className="text-2xs tabular-nums text-neutral-500">
          {fmt(c ? c.n : total)}
          <span className="sr-only"> istasyon</span>
        </span>
        <span aria-hidden className="grid w-3.5 shrink-0 place-items-center">
          {isSelected && <Check size={14} strokeWidth={2.2} className="text-accent-400" />}
        </span>
      </li>
    );
  };

  const groupHead = (text: string, hid: string): ReactNode => (
    <div id={hid} role="presentation" className="kicker px-3.5 pt-2.5 pb-1">
      {text}
    </div>
  );

  return (
    <div ref={root} className={`relative w-full sm:w-[220px] ${className}`}>
      <span id={labelId} className="sr-only">
        Ülke
      </span>
      <button
        ref={trigger}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-labelledby={`${labelId} ${id}-value`}
        onClick={() => (open ? close(false) : openPanel())}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            e.preventDefault();
            openPanel();
          }
        }}
        className="input flex cursor-pointer items-center gap-2 text-left"
      >
        {selected ? <Flag code={selected.code} /> : <AllIcon />}
        <span id={`${id}-value`} className="min-w-0 flex-1 truncate">
          {selected ? selected.name : value ? value.toUpperCase() : "Tüm ülkeler"}
          <span className="text-neutral-500">
            {" · "}
            {fmt(selected ? selected.n : value ? 0 : total)}
          </span>
        </span>
        <ChevronDown
          size={15}
          strokeWidth={1.9}
          aria-hidden
          className={`shrink-0 text-neutral-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className={`card animate-modal-in absolute top-full z-20 mt-1.5 flex flex-col overflow-hidden ${
            alignRight ? "right-0" : "left-0"
          }`}
          style={{
            width: `min(${PANEL_W}px, calc(100vw - ${GUTTER * 2}px))`,
            background: "var(--color-surface-2)",
            boxShadow: "var(--shadow-lg)",
          }}
        >
          <div className="relative border-b border-neutral-900 p-2">
            <Search
              size={14}
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-4.5 -translate-y-1/2 text-neutral-500"
            />
            <input
              ref={input}
              type="text"
              role="combobox"
              aria-label="Ülke ara"
              aria-autocomplete="list"
              aria-expanded
              aria-controls={listId}
              aria-activedescendant={activeCode !== undefined ? optId(activeCode) : undefined}
              placeholder="Ülke ara"
              autoComplete="off"
              spellCheck={false}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onInputKey}
              className="input !pl-8"
            />
          </div>

          <ul
            id={listId}
            role="listbox"
            aria-labelledby={labelId}
            className="max-h-[360px] overflow-y-auto overscroll-contain py-1"
          >
            {searching ? (
              rest.map((c) => renderOption(c.code))
            ) : (
              <>
                {renderOption("")}
                {pinned.length > 0 && (
                  <li role="group" aria-labelledby={`${id}-g-pin`}>
                    {groupHead("Sık kullanılan", `${id}-g-pin`)}
                    <ul role="presentation">{pinned.map((c) => renderOption(c.code))}</ul>
                  </li>
                )}
                <li role="group" aria-labelledby={`${id}-g-all`}>
                  {groupHead("Tüm ülkeler · istasyon sayısına göre", `${id}-g-all`)}
                  <ul role="presentation">{rest.map((c) => renderOption(c.code))}</ul>
                </li>
              </>
            )}
            {searching && flat.length === 0 && (
              <li role="presentation" className="px-3.5 py-6 text-center text-xs text-neutral-500">
                “{query.trim()}” için ülke bulunamadı
              </li>
            )}
          </ul>

          <p className="sr-only" role="status" aria-live="polite">
            {searching ? (flat.length ? `${flat.length} ülke` : "Sonuç yok") : ""}
          </p>
        </div>
      )}
    </div>
  );
}
